import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import billingService from '../../api/billingService';
import { formatDate, formatCurrency } from '../../utils/formatters';
import customerService from '../../api/customerService';

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [userRole, setUserRole] = useState('');

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentUploadModal, setShowPaymentUploadModal] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [paymentUploadRecords, setPaymentUploadRecords] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showMoreProfile, setShowMoreProfile] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    customerId: id,
    quotations: [], // Multiple quotes
    amount: 0,
    tds: 0,
    payment_type: 'credit', // credit, debit
    transactionId: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
    attachment: null
  });

  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const sessionRes = await customerService.getSession();
      if (sessionRes?.success) {
        setUserRole(sessionRes.userInfo?.role || '');
      }
      
      // Fetch customer first to ensure basic info is available
      const custRes = await billingService.getCustomerById(id);
      if (custRes.success) {
        setCustomer(custRes.data);
      } else {
        throw new Error('Customer not found');
      }

      // Fetch other data in parallel, but handle individual failures
      const [subRes, quoteRes, invRes, payRes, followupRes] = await Promise.allSettled([
        billingService.getSubscriptions({ customer_id: id }),
        billingService.getQuotes({ customer_id: id }),
        billingService.getInvoices({ customer_id: id }),
        billingService.getPaymentsByCustomer(id),
        billingService.getFollowups({ customer_id: id, status: 'all', limit: 5000 })
      ]);

      if (subRes.status === 'fulfilled' && subRes.value.success) setSubscriptions(subRes.value.data);
      if (quoteRes.status === 'fulfilled' && quoteRes.value.success) setQuotes(quoteRes.value.data);
      if (invRes.status === 'fulfilled' && invRes.value.success) setInvoices(invRes.value.data);
      if (payRes.status === 'fulfilled' && payRes.value.success) setPayments(payRes.value.data);
      if (followupRes.status === 'fulfilled' && followupRes.value.success) setFollowups(followupRes.value.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      let attachmentInfo = null;

      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const uploadRes = await customerService.uploadFile(formData);
        if (uploadRes.success && uploadRes.data && uploadRes.data.length > 0) {
          attachmentInfo = {
            _id: uploadRes.data[0]._id,
            fileName: uploadRes.data[0].fileName,
            fileSize: uploadRes.data[0].fileSize,
            fileType: uploadRes.data[0].fileType,
            s3Location: uploadRes.data[0].s3Location || uploadRes.data[0].url,
            localPath: uploadRes.data[0].localPath,
            url: uploadRes.data[0].url || uploadRes.data[0].s3Location
          };
        }
      }

      let res;
      if (editingPaymentId) {
        res = await billingService.updatePayment(editingPaymentId, {
          ...paymentFormData,
          attachment: attachmentInfo || paymentFormData.attachment
        });
      } else {
        res = await billingService.addPayment({
          ...paymentFormData,
          attachment: attachmentInfo
        });
      }

      if (res.success) {
        setShowPaymentModal(false);
        setEditingPaymentId(null);
        fetchData();
        setSelectedFile(null);
        setPaymentFormData({
          customerId: id,
          quotations: [],
          amount: 0,
          tds: 0,
          payment_type: 'credit',
          transactionId: '',
          paymentDate: new Date().toISOString().split('T')[0],
          notes: '',
          attachment: null
        });
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEditPayment = (pay) => {
    setEditingPaymentId(pay._id);
    setPaymentFormData({
      customerId: id,
      quotations: pay.quotations || [],
      amount: pay.amount,
      tds: pay.tds || 0,
      payment_type: pay.payment_type,
      transactionId: pay.reference_number || pay.transactionId || '',
      paymentDate: pay.transaction_date ? new Date(pay.transaction_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      notes: pay.notes || '',
      attachment: pay.attachment || null
    });
    setShowPaymentModal(true);
  };

  const handleQuoteSelection = (quoteId) => {
    const isSelected = paymentFormData.quotations.includes(quoteId);
    let updatedQuotes = [];
    if (isSelected) {
      updatedQuotes = paymentFormData.quotations.filter(q => q !== quoteId);
    } else {
      updatedQuotes = [...paymentFormData.quotations, quoteId];
    }

    // Calculate total amount from selected quotes
    const total = quotes
      .filter(q => updatedQuotes.includes(q._id))
      .reduce((sum, q) => sum + (q.totalAmount || 0), 0);

    setPaymentFormData({
      ...paymentFormData,
      quotations: updatedQuotes,
      amount: total
    });
  };

  const getAttachmentUrl = (attachment) => {
    if (!attachment) return '';
    return customerService.resolveAttachmentUrl(attachment);
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!attachment) return;
    await customerService.downloadAttachment(attachment);
  };

  const getAttachmentName = (attachment) => {
    if (!attachment) return '';
    if (typeof attachment === 'string') {
      const cleaned = attachment.split('?')[0].split('#')[0];
      return cleaned.split('/').pop() || 'Attachment';
    }
    return (
      attachment.file_name ||
      attachment.fileName ||
      attachment.original_name ||
      attachment.name ||
      ((attachment.url || attachment.s3Location || attachment.localPath || '').split('/').pop().split('?')[0].split('#')[0]) ||
      'Attachment'
    );
  };

  const getAttachmentSize = (attachment) => {
    if (!attachment || typeof attachment === 'string') return null;
    const raw = attachment.file_size ?? attachment.fileSize ?? attachment.size ?? null;
    return raw == null ? null : raw;
  };

  const getNormalizedCustomerAttachments = (cust) => {
    if (!cust) return [];
    const list = cust.attachments || cust.documents || cust.Attachments || cust.Documents || [];
    if (!Array.isArray(list) || list.length === 0) return [];
    return list.map((att) => {
      if (typeof att === 'string') {
        if (att.startsWith('data:') || att.startsWith('http://') || att.startsWith('https://') || att.startsWith('/')) {
          return {
            file_name: (att.split('/').pop() || 'attachment').split('?')[0].split('#')[0] || 'attachment',
            url: att,
            s3Location: att
          };
        }
        return { file_name: att, url: att, s3Location: att };
      }
      return att;
    });
  };

  const exportPaymentsToExcel = () => {
    const exportData = payments.map((pay) => ({
      customerId: customer?.customerId || id,
      notes: pay.notes || '',
      amount: pay.amount || 0,
      payment_type: pay.payment_type || '',
      transaction_date: pay.transaction_date ? new Date(pay.transaction_date).toISOString().split('T')[0] : '',
      tds: pay.tds || 0,
      reference_number: pay.reference_number || pay.transactionId || '',
      created_at: pay.created_at ? new Date(pay.created_at).toISOString() : '',
      updated_at: pay.updated_at ? new Date(pay.updated_at).toISOString() : ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payments');
    XLSX.writeFile(wb, `Payments_Export_${customer?.customerId || id}.xlsx`);
  };

  const handlePaymentFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);
      setPaymentUploadRecords(data);
    };
    reader.readAsBinaryString(file);
  };

  const handlePaymentUploadSubmit = async () => {
    try {
      setUploading(true);
      let successCount = 0;
      for (const rec of paymentUploadRecords) {
        const amount = parseFloat(rec.amount) || 0;
        if (amount <= 0) continue;
        await billingService.addPayment({
          customerId: rec.customerId || rec.customer_id || id,
          notes: rec.notes || '',
          amount,
          payment_type: rec.payment_type || 'credit',
          paymentDate: rec.transaction_date || rec.paymentDate || rec.transactionDate || new Date().toISOString().split('T')[0],
          tds: parseFloat(rec.tds) || 0,
          reference_number: rec.reference_number || rec.transactionId || '',
          created_at: rec.created_at || undefined,
          updated_at: rec.updated_at || rec.updated_At || undefined
        });
        successCount += 1;
      }
      setShowPaymentUploadModal(false);
      setPaymentUploadRecords([]);
      await fetchData();
      alert(`${successCount} payment records uploaded successfully.`);
    } catch (error) {
      alert(error.message);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="p-5 text-center">Loading...</div>;
  if (!customer) return <div className="p-5 text-center text-danger">Customer not found.</div>;

  const getQuoteProductNames = (q) => {
    const names = (q?.items || [])
      .map(it => it?.productName)
      .filter(Boolean);
    if (names.length === 0) return 'N/A';
    return names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
  };


  // Calculate stats based on payments and quotations
  const totalBilled = quotes
    .filter(q => q.status === 'active' || q.status === 'approved')
    .reduce((sum, q) => sum + (q.totalAmount || 0), 0);

  const totalPaid = payments
    .filter(p => p.payment_type === 'credit')
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0) + (parseFloat(p.tds) || 0), 0);
  
  const totalDebits = payments
    .filter(p => p.payment_type === 'debit')
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  
  const actualPaid = totalPaid - totalDebits;
  const pendingAmount = totalBilled - actualPaid;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">

        <div className="d-flex gap-2">
          <span className={`badge badge-soft-${customer.status === 'active' ? 'success' : 'danger'} align-self-center me-2`}>
            {customer.status?.toUpperCase()}
          </span>

        </div>
      </div>

      {/* Stats Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm bg-primary text-white">
            <div className="card-body p-4 text-center">
              <div className="text-xs fw-700 uppercase mb-1 opacity-75">TOTAL BILLED</div>
              <h4 className="fw-800 mb-0">{formatCurrency(totalBilled)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm bg-success text-white">
            <div className="card-body p-4 text-center">
              <div className="text-xs fw-700 uppercase mb-1 opacity-75">TOTAL PAID</div>
              <h4 className="fw-800 mb-0">{formatCurrency(actualPaid)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm bg-danger text-white">
            <div className="card-body p-4 text-center">
              <div className="text-xs fw-700 uppercase mb-1 opacity-75">PENDING AMOUNT</div>
              <h4 className="fw-800 mb-0">{formatCurrency(pendingAmount)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm bg-info text-white">
            <div className="card-body p-4 text-center">
              <div className="text-xs fw-700 uppercase mb-1 opacity-75">ACTIVE SERVICES</div>
              <h4 className="fw-800 mb-0">{subscriptions.filter(s => s.status === 'active').length}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-0 pt-3 px-4">
          <ul className="nav nav-pills card-header-pills">
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'profile' ? 'active' : 'text-muted'}`} onClick={() => setActiveTab('profile')}>Profile</button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'services' ? 'active' : 'text-muted'}`} onClick={() => setActiveTab('services')}>Services</button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'quotations' ? 'active' : 'text-muted'}`} onClick={() => setActiveTab('quotations')}>Quotations</button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'payments' ? 'active' : 'text-muted'}`} onClick={() => setActiveTab('payments')}>Payments History</button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'followups' ? 'active' : 'text-muted'}`} onClick={() => setActiveTab('followups')}>Follow-ups</button>
            </li>
          </ul>
        </div>
        
        <div className="card-body p-4">
          {activeTab === 'profile' && (
            <div className="row g-4">
              <div className="col-md-8">

                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">COMPANY NAME</label>
                    <div className="fw-600">{customer.company || 'N/A'}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">GST NUMBER</label>
                    <div className="fw-600">{customer.gstNumber || 'N/A'}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">ASSIGNED TO</label>
                    <div className="fw-600 text-info">{(() => {
                      const v = customer.assignTo;
                      if (v == null) return 'Unassigned';
                      const arr = Array.isArray(v) ? v : String(v).split(',').map((s) => s.trim()).filter(Boolean);
                      return arr.length ? arr.join(', ') : 'Unassigned';
                    })()}</div>
                  </div>
                  <div className="col-12">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">ADDRESS</label>
                    <div className="fw-600">{customer.address || 'N/A'}</div>
                  </div>
                  <div className="col-12 mt-2">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">
                      DOCUMENTS / ATTACHMENTS ({getNormalizedCustomerAttachments(customer).length})
                    </label>
                    {getNormalizedCustomerAttachments(customer).length === 0 ? (
                      <div className="text-xs text-muted fst-italic p-3 bg-light rounded-3 text-center">
                        No documents attached yet.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {getNormalizedCustomerAttachments(customer).map((att, idx) => {
                          const name = getAttachmentName(att) || `Document ${idx + 1}`;
                          const size = getAttachmentSize(att);
                          return (
                            <div
                              key={att._id ? `${att._id}-${idx}` : idx}
                              className="d-flex justify-content-between align-items-center p-2 bg-light rounded-3"
                            >
                              <button
                                type="button"
                                onClick={() => handleDownloadAttachment(att)}
                                className="text-decoration-none d-flex align-items-center gap-2 text-xs fw-600 border-0 bg-transparent text-start p-0"
                                style={{ minWidth: 0, overflow: 'hidden', color: 'inherit', cursor: 'pointer' }}
                              >
                                <i className="bi bi-paperclip text-primary"></i>
                                <span className="text-truncate">{name}</span>
                                {size !== null && (
                                  <span className="text-muted fw-400">
                                    ({size < 1024 * 1024
                                      ? `${Math.round(size / 1024)} KB`
                                      : `${(size / (1024 * 1024)).toFixed(1)} MB`})
                                  </span>
                                )}
                                <i className="bi bi-download text-muted"></i>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                  <h6 className="fw-700 mb-0">Business Information</h6>
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none p-0"
                    onClick={() => setShowMoreProfile((prev) => !prev)}
                  >
                    {showMoreProfile ? 'Less' : 'More'}
                  </button>
                </div>
                  {showMoreProfile && (
                    <>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">CUSTOMER ID</label>
                        <div className="fw-600">{customer.customerId || customer._id || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">SOLD BY</label>
                        <div className="fw-600">{customer.soldBy || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">CONTACT PERSON</label>
                        <div className="fw-600">{customer.contactPerson || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">ACCOUNT TYPE</label>
                        <div className="fw-600">{customer.accountType || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">INDUSTRY</label>
                        <div className="fw-600">{customer.industry || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">LEAD SOURCE</label>
                        <div className="fw-600">{customer.leadSource || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">COMPANY SIZE</label>
                        <div className="fw-600">{customer.company_size || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">WEBSITE</label>
                        <div className="fw-600">{customer.website || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">STATE</label>
                        <div className="fw-600">{customer.state || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">PINCODE</label>
                        <div className="fw-600">{customer.pincode || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">LANDMARK</label>
                        <div className="fw-600">{customer.landmark || 'N/A'}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">STATUS</label>
                        <div className="fw-600">{customer.status || 'N/A'}</div>
                      </div>
                      <div className="col-12">
                        <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">REMARK</label>
                        <div className="fw-600">{customer.remark || 'N/A'}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <h6 className="fw-700 mb-3">Contact Details</h6>
                <div className="mb-3">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">EMAIL</label>
                  <div className="fw-600 text-primary">{customer.email}</div>
                </div>
                <div className="mb-3">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">PHONE</label>
                  <div className="fw-600">{customer.phone}</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Service Name</th>
                    <th>Product</th>
                    <th>Start Date</th>
                    <th>Renewal Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-4">No active services found.</td></tr>
                  ) : (
                    subscriptions.map(sub => (
                      <tr key={sub._id}>
                        <td className="fw-600 text-primary cursor-pointer" onClick={() => navigate(`/billing/subscriptions/edit/${sub._id}`)}>
                          {sub.custom_alias || 'N/A'}
                        </td>
                        <td className="text-xs">{sub.productData?.productName || sub.product?.productName || sub.product_name || 'N/A'}</td>
                        <td className="text-xs">{formatDate(sub.start_date)}</td>
                        <td className="text-xs text-danger fw-600">{formatDate(sub.next_renewal_date)}</td>
                        <td className="fw-600">{formatCurrency(sub.amount)}</td>
                        <td><span className={`badge badge-soft-${sub.status === 'active' ? 'success' : 'warning'}`}>{sub.status?.toUpperCase()}</span></td>
                        <td>
                          <button className="btn btn-sm btn-light text-primary" onClick={() => { setSelectedService(sub); setShowServiceModal(true); }}>
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'quotations' && (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Quote #</th>
                    <th>Product</th>
                    <th>Date</th>
                    <th>Valid Until</th>
                    <th>Total Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-4">No quotations found.</td></tr>
                  ) : (
                    quotes.map(q => (
                      <tr key={q._id}>
                        <td className="fw-600 text-primary cursor-pointer" onClick={() => navigate(`/billing/quotes/view/${q._id}`)}>
                          {q.quotationNumber}
                        </td>
                        <td className="text-xs">{getQuoteProductNames(q)}</td>
                        <td className="text-xs">{formatDate(q.quotationDate)}</td>
                        <td className="text-xs">{formatDate(q.validUntil)}</td>
                        <td className="fw-600">{formatCurrency(q.totalAmount)}</td>
                        <td><span className={`badge badge-soft-${q.status === 'active' ? 'success' : 'warning'}`}>{q.status?.toUpperCase()}</span></td>
                        <td><button className="btn btn-sm btn-light" onClick={() => navigate(`/billing/quotes/view/${q._id}`)}><i className="bi bi-eye"></i></button></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'payments' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-700 mb-0">Transaction History</h6>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-light border" onClick={() => setShowPaymentUploadModal(true)}>
                    <i className="bi bi-upload me-1"></i> Upload
                  </button>
                  <button className="btn btn-sm btn-light border" onClick={exportPaymentsToExcel}>
                    <i className="bi bi-download me-1"></i> Export
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={() => setShowPaymentModal(true)}>
                    <i className="bi bi-plus-lg me-1"></i> Add Payment
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th>Date</th>
                      <th>Reference / Notes</th>
                      <th>Type</th>
                      <th>TDS</th>
                      <th>Amount</th>
                      <th>File</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 ? (
                      <tr><td colSpan="6" className="text-center py-4">No payment history found.</td></tr>
                    ) : (
                      payments.map(pay => (
                        <tr key={pay._id}>
                          <td className="text-xs">{formatDate(pay.transaction_date)}</td>
                          <td>
                            <div className="text-xs fw-600">{pay.transactionId || pay.reference_number || 'N/A'}</div>
                            <div className="text-xs text-muted">{pay.notes}</div>
                            {pay.quotations?.length > 0 && (
                              <div className="mt-1">
                                {pay.quotations.map(qId => {
                                  const q = quotes.find(quote => quote._id === qId);
                                  return q ? <span key={qId} className="badge bg-light text-dark border me-1 text-xs">{q.quotationNumber}</span> : null;
                                })}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`badge badge-soft-${pay.payment_type === 'credit' ? 'success' : 'danger'}`}>
                              {pay.payment_type?.toUpperCase()}
                            </span>
                          </td>
                          <td className="text-xs fw-600 text-danger">{pay.tds > 0 ? formatCurrency(pay.tds) : '-'}</td>
                          <td className={`fw-600 ${pay.payment_type === 'credit' ? 'text-success' : 'text-danger'}`}>
                            {pay.payment_type === 'credit' ? '+' : '-'}{formatCurrency(pay.amount)}
                          </td>
                          <td>
                            {pay.attachment && getAttachmentUrl(pay.attachment) && (
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light text-primary"
                                  onClick={() => handleDownloadAttachment(pay.attachment)}
                                  title={`Download ${getAttachmentName(pay.attachment)}`}
                                >
                                  <i className="bi bi-download me-1"></i>
                                  <span className="small fw-600">
                                    {getAttachmentName(pay.attachment) || 'Download'}
                                  </span>
                                </button>
                                <a
                                  href={getAttachmentUrl(pay.attachment)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-muted text-decoration-underline"
                                >
                                  open
                                </a>
                              </div>
                            )}
                          </td>
                          <td>
                            <button className="btn btn-sm btn-light text-primary" onClick={() => handleEditPayment(pay)}>
                              <i className="bi bi-pencil"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'followups' && (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Feedback</th>
                    <th>Usage</th>
                    <th>Comment</th>
                    <th>Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {followups.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-5 fst-italic">No follow-ups yet.</td>
                    </tr>
                  ) : (
                    followups
                      .slice()
                      .sort((a, b) => new Date(b.created_at || b.date || b.followupDateTime || 0) - new Date(a.created_at || a.date || a.followupDateTime || 0))
                      .map(f => {
                        const status = (f.status || 'pending').toLowerCase();
                        const badgeColor = status === 'completed'
                          ? 'success'
                          : status === 'cancelled'
                            ? 'danger'
                            : status === 'scheduled'
                              ? 'info'
                              : 'warning';
                        return (
                          <tr key={f._id}>
                            <td>{formatDate(f.followupDateTime || f.created_at || f.date)}</td>
                            <td>
                              <span className={`badge badge-soft-${badgeColor}`}>
                                {(f.status || 'Pending').toUpperCase()}
                              </span>
                            </td>
                            <td><span className="badge badge-soft-info">{f.feedback || 'N/A'}</span></td>
                            <td><span className="badge badge-soft-secondary">{f.usage || 'N/A'}</span></td>
                            <td className="text-xs">{f.comment || f.comments || 'No comment'}</td>
                            <td className="text-xs">
                              {status === 'completed'
                                ? (
                                  <div>
                                    <div className="fw-600 text-success">{formatDate(f.completedAt || f.updated_at || f.date)}</div>
                                    {f.completionComment && <div className="text-muted mt-1">{f.completionComment}</div>}
                                  </div>
                                )
                                : (
                                  <span className="text-muted fst-italic">Pending</span>
                                )}
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <form onSubmit={handlePaymentSubmit}>
                <div className="modal-header border-0 p-4">
                  <h5 className="modal-title fw-800">{editingPaymentId ? 'Edit Payment' : 'Record New Payment'}</h5>
                  <button type="button" className="btn-close" onClick={() => { setShowPaymentModal(false); setEditingPaymentId(null); }}></button>
                </div>
                <div className="modal-body p-4">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">PAYMENT TYPE</label>
                      <select className="form-select" value={paymentFormData.payment_type} onChange={e => setPaymentFormData({...paymentFormData, payment_type: e.target.value})}>
                        <option value="credit">Credit (Payment Received)</option>
                        <option value="debit">Debit (Refund/Chargeback)</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">PAYMENT DATE</label>
                      <input type="date" className="form-control" value={paymentFormData.paymentDate} onChange={e => setPaymentFormData({...paymentFormData, paymentDate: e.target.value})} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">TRANSACTION ID / REF</label>
                      <input type="text" className="form-control" value={paymentFormData.transactionId} onChange={e => setPaymentFormData({...paymentFormData, transactionId: e.target.value})} placeholder="e.g. TXN123456" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted small fw-600">AMOUNT</label>
                      <input type="number" className="form-control" value={paymentFormData.amount} onChange={e => setPaymentFormData({...paymentFormData, amount: parseFloat(e.target.value) || 0})} required />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted small fw-600">TDS (IF ANY)</label>
                      <input type="number" className="form-control" value={paymentFormData.tds} onChange={e => setPaymentFormData({...paymentFormData, tds: parseFloat(e.target.value) || 0})} />
                    </div>
                    
                    <div className="col-12">
                      <label className="form-label text-muted small fw-600 mb-3">LINK TO QUOTATIONS (OPTIONAL)</label>
                      <div className="row g-2">
                        {quotes.filter(q => q.status === 'active' || q.status === 'approved').map(q => (
                          <div key={q._id} className="col-md-6">
                            <div 
                              className={`p-3 border rounded cursor-pointer d-flex justify-content-between align-items-center ${paymentFormData.quotations.includes(q._id) ? 'border-primary bg-light' : ''}`}
                              onClick={() => handleQuoteSelection(q._id)}
                            >
                              <div>
                                <div className="fw-600 small">{q.quotationNumber}</div>
                                <div className="text-xs text-muted">{formatDate(q.quotationDate)}</div>
                              </div>
                              <div className="fw-700">{formatCurrency(q.totalAmount)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label text-muted small fw-600">NOTES</label>
                      <textarea className="form-control" rows="2" value={paymentFormData.notes} onChange={e => setPaymentFormData({...paymentFormData, notes: e.target.value})}></textarea>
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small fw-600">ATTACHMENT</label>
                      <input type="file" className="form-control" onChange={e => setSelectedFile(e.target.files[0])} />
                      {paymentFormData.attachment && !selectedFile && (
                        <div className="text-xs text-success mt-1">Existing attachment: {getAttachmentName(paymentFormData.attachment)}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 p-4">
                  <button type="button" className="btn btn-light px-4" onClick={() => { setShowPaymentModal(false); setEditingPaymentId(null); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary px-4" disabled={uploading || (paymentFormData.amount <= 0 && paymentFormData.payment_type === 'credit')}>
                    {uploading ? 'Processing...' : (editingPaymentId ? 'Update Payment' : 'Submit Payment')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showPaymentUploadModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 p-4 pb-0">
                <h5 className="modal-title fw-800">Upload Payments</h5>
                <button type="button" className="btn-close" onClick={() => { setShowPaymentUploadModal(false); setPaymentUploadRecords([]); }}></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-info small mb-3">
                  Columns: <strong>customerId</strong>, <strong>notes</strong>, <strong>amount</strong>, <strong>payment_type</strong>, <strong>transaction_date</strong>, <strong>tds</strong>, <strong>reference_number</strong>, <strong>created_at</strong>, <strong>updated_at</strong>
                </div>
                <input
                  type="file"
                  className="form-control mb-3"
                  accept=".xlsx, .xls, .csv"
                  onChange={handlePaymentFileUpload}
                />
                {paymentUploadRecords.length > 0 && (
                  <div className="text-success small fw-600">
                    {paymentUploadRecords.length} payment records detected.
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 p-4 pt-0">
                <button type="button" className="btn btn-light px-4" onClick={() => { setShowPaymentUploadModal(false); setPaymentUploadRecords([]); }}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  disabled={paymentUploadRecords.length === 0 || uploading}
                  onClick={handlePaymentUploadSubmit}
                >
                  {uploading ? 'Uploading...' : 'Start Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Details Modal */}
      {showServiceModal && selectedService && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 p-4">
                <h5 className="modal-title fw-800">Service Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowServiceModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-4">
                  <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">SERVICE ALIAS</label>
                  <div className="fw-700 text-primary h5">{selectedService.custom_alias || 'N/A'}</div>
                </div>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">PRODUCT</label>
                    <div className="fw-600">{selectedService.productData?.productName || selectedService.product?.productName || selectedService.product_name || 'N/A'}</div>
                  </div>
                  <div className="col-6">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">AMOUNT</label>
                    <div className="fw-600">{formatCurrency(selectedService.amount)}</div>
                  </div>
                  <div className="col-6">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">START DATE</label>
                    <div className="fw-600">{formatDate(selectedService.start_date)}</div>
                  </div>
                  <div className="col-6">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">RENEWAL DATE</label>
                    <div className="fw-600 text-danger">{formatDate(selectedService.next_renewal_date)}</div>
                  </div>
                  <div className="col-12">
                    <label className="text-muted text-xs fw-600 mb-1 d-block uppercase">COMMENT</label>
                    <div className="fw-600 p-2 bg-light rounded small">{selectedService.comment || 'No comment provided.'}</div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 p-4">
                {userRole === 'owner' && (
                  <button type="button" className="btn btn-primary px-4" onClick={() => navigate(`/billing/subscriptions/edit/${selectedService._id}`)}>Edit Service</button>
                )}
                <button type="button" className="btn btn-light px-4" onClick={() => setShowServiceModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetails;
